import { API_CONFIG, createApiUrl } from '../config/api';
import type { LocalAuthErrorPayload, LocalAuthField } from '../utils/localAuthErrors';
import { resolveLocalAuthMessage } from '../utils/localAuthErrors';
import { getReferralCode, clearReferralCode } from '../utils/referralStorage';

type ErrorBody = Record<string, unknown>;

export class LocalAuthApiError extends Error implements LocalAuthErrorPayload {
  status: number;
  errorCode?: string;
  field?: LocalAuthField;
  emailVerificationRequired?: boolean;
  retryAfter?: number;

  constructor(
    message: string,
    status: number,
    options?: {
      errorCode?: string;
      field?: LocalAuthField;
      emailVerificationRequired?: boolean;
      retryAfter?: number;
    }
  ) {
    super(message);
    this.name = 'LocalAuthApiError';
    this.status = status;
    this.errorCode = options?.errorCode;
    this.field = options?.field;
    this.emailVerificationRequired = options?.emailVerificationRequired;
    this.retryAfter = options?.retryAfter;
  }

  toPayload(): LocalAuthErrorPayload {
    return {
      message: this.message,
      status: this.status,
      errorCode: this.errorCode,
      field: this.field,
      emailVerificationRequired: this.emailVerificationRequired,
      retryAfter: this.retryAfter,
    };
  }
}

function parseRetryAfter(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds : undefined;
}

function buildLocalAuthError(response: Response, data: ErrorBody = {}): LocalAuthApiError {
  const errorCode = typeof data.errorCode === 'string' ? data.errorCode : undefined;
  const field = typeof data.field === 'string' ? (data.field as LocalAuthField) : undefined;
  const retryAfter = parseRetryAfter(response);
  const emailVerificationRequired =
    data.emailVerificationRequired === true || errorCode === 'EMAIL_NOT_VERIFIED';

  const message = resolveLocalAuthMessage({
    message: typeof data.message === 'string' ? data.message : undefined,
    errorCode,
    status: response.status,
    retryAfter,
  });

  return new LocalAuthApiError(message, response.status, {
    errorCode,
    field,
    emailVerificationRequired,
    retryAfter,
  });
}

export function networkLocalAuthError(): LocalAuthApiError {
  return new LocalAuthApiError(
    resolveLocalAuthMessage({ status: 0 }),
    0
  );
}

async function localAuthFetch(
  url: string,
  init: RequestInit
): Promise<{ response: Response; data: ErrorBody }> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    throw networkLocalAuthError();
  }

  const data = (await response.json().catch(() => ({}))) as ErrorBody;
  return { response, data };
}

export interface RegisterPayload {
  login: string;
  password: string;
  email: string;
  captchaToken?: string;
}

function appendCaptcha<T extends Record<string, unknown>>(
  body: T,
  captchaToken?: string
): T {
  if (!captchaToken) return body;
  return { ...body, captchaToken };
}

export async function registerLocal(payload: RegisterPayload) {
  const { captchaToken, ...rest } = payload;
  const referralCode = getReferralCode();
  const body = appendCaptcha(
    referralCode ? { ...rest, referralCode } : rest,
    captchaToken
  );

  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_REGISTER),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    }
  );

  if (!response.ok || data.success === false) {
    throw buildLocalAuthError(response, data);
  }

  clearReferralCode();

  return data as {
    success: true;
    message: string;
    emailVerificationRequired: boolean;
  };
}

export async function loginLocal(payload: {
  login: string;
  password: string;
  captchaToken?: string;
}) {
  const { captchaToken, ...rest } = payload;
  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_LOGIN),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(appendCaptcha(rest, captchaToken)),
    }
  );

  if (!response.ok || data.success === false) {
    throw buildLocalAuthError(response, data);
  }

  return data as Record<string, unknown> & {
    accessToken: string;
    user: unknown;
    isNewUser?: boolean;
  };
}

export async function verifyLocalEmail(token: string) {
  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_VERIFY_EMAIL),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    }
  );

  if (!response.ok || !data.success) {
    throw buildLocalAuthError(response, data);
  }

  return data as Record<string, unknown> & {
    accessToken: string;
    user: unknown;
    isNewUser?: boolean;
  };
}

export async function resendVerificationEmail(
  loginOrEmail: string,
  captchaToken?: string
) {
  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_RESEND_VERIFICATION),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(
        appendCaptcha({ loginOrEmail: loginOrEmail.trim() }, captchaToken)
      ),
    }
  );

  if (!response.ok) {
    throw buildLocalAuthError(response, data);
  }

  return data as {
    success: true;
    message: string;
    emailVerificationRequired?: boolean;
  };
}

export async function forgotPassword(loginOrEmail: string, captchaToken?: string) {
  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_FORGOT_PASSWORD),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(
        appendCaptcha({ loginOrEmail: loginOrEmail.trim() }, captchaToken)
      ),
    }
  );

  if (!response.ok) {
    throw buildLocalAuthError(response, data);
  }

  return data as { success: true; message: string };
}

export async function validateResetPasswordToken(token: string) {
  const url = createApiUrl(
    `${API_CONFIG.ENDPOINTS.AUTH.LOCAL_RESET_PASSWORD_VALIDATE}?token=${encodeURIComponent(token)}`
  );

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET', credentials: 'include' });
  } catch {
    throw networkLocalAuthError();
  }

  const data = (await response.json().catch(() => ({}))) as ErrorBody;

  if (!response.ok || !data.success) {
    throw buildLocalAuthError(response, data);
  }

  return data as { success: true; message: string };
}

export async function resetLocalPassword(token: string, password: string) {
  const { response, data } = await localAuthFetch(
    createApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOCAL_RESET_PASSWORD),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token, password }),
    }
  );

  if (!response.ok || !data.success) {
    throw buildLocalAuthError(response, data);
  }

  return data as Record<string, unknown> & {
    accessToken: string;
    user: unknown;
    isNewUser?: boolean;
  };
}
